import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, TextField, Button, Typography, Box,
  Alert, InputAdornment, IconButton, Divider, 
  useTheme, alpha, CircularProgress, Link,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { 
  Visibility, VisibilityOff, Email, Lock,
  MusicNote, CameraAlt, Mic
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import LogoSVG from './common/LogoSVG';

function Login() {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(emailOrUsername, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const FloatingShape = ({ icon, delay, duration, size, position }) => (
    <Box
      sx={{
        position: 'absolute',
        ...position,
        animation: `float ${duration}s ease-in-out ${delay}s infinite`,
        opacity: 0.1,
        color: theme.palette.primary.main,
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-20px)' },
        },
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: size } })}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <FloatingShape 
        icon={<MusicNote />} 
        delay={0} 
        duration={6} 
        size={80} 
        position={{ top: '10%', left: '10%' }} 
      />
      <FloatingShape 
        icon={<CameraAlt />} 
        delay={2} 
        duration={8} 
        size={100} 
        position={{ top: '60%', right: '15%' }} 
      />
      <FloatingShape 
        icon={<Mic />} 
        delay={4} 
        duration={7} 
        size={90} 
        position={{ bottom: '20%', left: '20%' }} 
      />

      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <LogoSVG width={250} height={75} />
          </Box>

          <Divider sx={{ mb: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Professional Studio Management
            </Typography>
          </Divider>

          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in to access your studio dashboard
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email or Username"
              variant="outlined"
              margin="normal"
              required
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #0066CC 30%, #0080FF 90%)',
                boxShadow: '0 3px 5px 2px rgba(0, 102, 204, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0052A3 30%, #0066CC 90%)',
                  boxShadow: '0 4px 8px 3px rgba(0, 102, 204, .3)',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link 
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setContactOpen(true);
                }}
                sx={{ 
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link 
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setContactOpen(true);
                  }}
                  sx={{ 
                    color: theme.palette.primary.main, 
                    textDecoration: 'none',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Contact Admin
                </Link>
              </Typography>
            </Box>
          </form>

          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              © 2025 Omega Studio. All rights reserved.
            </Typography>
          </Box>
        </Paper>

        <Dialog open={contactOpen} onClose={() => setContactOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Contact Administrator</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              To request an account or reset your password, please contact the administrator:
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Email:</strong> sam@omegarecordingstudio.com
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Phone:</strong> +234 XXX XXX XXXX
              </Typography>
              <Typography variant="body2">
                <strong>Office Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              For password resets, please provide your username and registered email address.
              For new accounts, please provide your full name, email, and department.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactOpen(false)}>Close</Button>
            <Button 
              variant="contained" 
              onClick={() => {
                window.location.href = 'mailto:sam@omegarecordingstudio.com?subject=Omega Studio - Account Request';
              }}
              sx={{
                background: 'linear-gradient(45deg, #0066CC 30%, #0080FF 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0052A3 30%, #0066CC 90%)',
                },
              }}
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default Login;
